import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { Plus } from 'lucide-react';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { AdminSectionCard } from '@/components/admin/AdminSectionCard';
import { CampaignEmailPanel } from '@/components/campaigns/CampaignEmailPanel';
import { CampaignPreview } from '@/components/campaigns/CampaignPreview';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  createCampaign,
  deleteCampaign,
  listCampaigns,
  updateCampaign,
} from '@/services/campaigns';
import type { AdminCampaign, AdminCampaignInput, CampaignTheme } from '@/services/types';
import type { CampaignFormInput, CampaignFormOutput } from '@/lib/schemas/campaign-schemas';
import { campaignFormSchema } from '@/lib/schemas/campaign-schemas';
import type { SubscriptionTier } from '@atsready/shared';

const CAMPAIGN_QUERY_KEY = ['campaigns'] as const;
const TARGET_TIERS: SubscriptionTier[] = ['FREE', 'BASIC', 'PRO', 'PRO_PLUS'];
const TIER_LABELS: Record<SubscriptionTier, string> = {
  FREE: 'Free',
  BASIC: 'Basic',
  PRO: 'Pro',
  PRO_PLUS: 'PRO Plus',
};

/**
 * The blank-and-safe defaults a New Campaign form opens with.
 *
 * `name` and `slug` are empty on purpose: they were `'Spring promo'` and
 * `'spring-promo'`, so every operator who did not overwrite them created a row
 * with the same identity, and the slug is the identity. The form validates on
 * submit, so blank fields are a draft rather than a wall of red.
 *
 * `isActive: false` is the one default that makes a new campaign safe. A
 * campaign published the moment it is saved outranks the seeded default the
 * product ships - and `priority: 0` alone does not fix that, because
 * `sortCampaignRows` breaks a priority tie by `updatedAt` descending, so a
 * campaign saved today sorts ahead of the seeded row at equal priority simply
 * by being newer. Both ship; only the first is load-bearing.
 *
 * The copy fields carry the seeded campaign's own strings, so the preview shows
 * what a reader would actually see rather than placeholder prose, and
 * `emailBody` carries a template key because that is what the field is.
 */
function createDefaultFormValues(): CampaignFormInput {
  return {
    name: '',
    slug: '',
    bannerTitle: 'Unlock the full ATS Ready',
    bannerDescription:
      'Basic adds the AI résumé builder. Pro adds ATS scoring, job matching, tailoring and the Chrome extension.',
    bannerCtaText: 'See the plans',
    bannerCtaUrl: '/pricing',
    interstitialTitle: 'Unlock the full ATS Ready',
    interstitialDescription:
      'Basic adds the AI résumé builder. Pro adds ATS scoring, job matching, tailoring, the Chrome extension and a monthly allowance of job imports. Pick the one that fits how you are searching.',
    interstitialCtaText: 'See the plans',
    interstitialCtaUrl: '/pricing',
    emailSubject: 'Unlock the full ATS Ready',
    emailBody: 'subscription/default-free-trial',
    theme: {
      accent: '#2a85ff',
      background: '#eff6ff',
      foreground: '#0f172a',
      icon: 'sparkles',
    },
    discountPercent: '',
    discountDurationMonths: '',
    targetTiers: ['FREE'],
    startDate: '',
    endDate: '',
    isActive: false,
    priority: 0,
  };
}

function toFormValues(campaign: AdminCampaign | null): CampaignFormInput {
  if (!campaign) {
    return createDefaultFormValues();
  }

  return {
    name: campaign.name,
    slug: campaign.slug,
    bannerTitle: campaign.bannerTitle,
    bannerDescription: campaign.bannerDescription,
    bannerCtaText: campaign.bannerCtaText,
    bannerCtaUrl: campaign.bannerCtaUrl,
    interstitialTitle: campaign.interstitialTitle,
    interstitialDescription: campaign.interstitialDescription,
    interstitialCtaText: campaign.interstitialCtaText,
    interstitialCtaUrl: campaign.interstitialCtaUrl,
    emailSubject: campaign.emailSubject,
    emailBody: campaign.emailBody,
    theme: {
      ...campaign.theme,
      icon: campaign.theme.icon ?? 'sparkles',
    },
    discountPercent: campaign.discountPercent ?? '',
    discountDurationMonths: campaign.discountDurationMonths ?? '',
    targetTiers: campaign.targetTiers,
    startDate: campaign.startDate ?? '',
    endDate: campaign.endDate ?? '',
    isActive: campaign.isActive,
    priority: campaign.priority,
  };
}

function normalizeCampaign(values: CampaignFormOutput): AdminCampaignInput {
  return {
    name: values.name,
    slug: values.slug,
    bannerTitle: values.bannerTitle,
    bannerDescription: values.bannerDescription,
    bannerCtaText: values.bannerCtaText,
    bannerCtaUrl: values.bannerCtaUrl,
    interstitialTitle: values.interstitialTitle,
    interstitialDescription: values.interstitialDescription,
    interstitialCtaText: values.interstitialCtaText,
    interstitialCtaUrl: values.interstitialCtaUrl,
    emailSubject: values.emailSubject,
    emailBody: values.emailBody,
    theme: values.theme,
    discountPercent: values.discountPercent ?? null,
    discountDurationMonths: values.discountDurationMonths ?? null,
    targetTiers: values.targetTiers,
    startDate: values.startDate ?? null,
    endDate: values.endDate ?? null,
    isActive: values.isActive,
    priority: values.priority,
  };
}

function getSyncBadgeVariant(status: AdminCampaign['stripeSyncStatus']) {
  switch (status) {
    case 'synced':
      return 'success';
    case 'pending':
      return 'warning';
    case 'failed':
      return 'destructive';
    case 'not_applicable':
      return 'outline';
  }
}

export default function CampaignManager() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [editingCampaign, setEditingCampaign] = useState<AdminCampaign | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminCampaign | null>(null);

  const campaignsQuery = useQuery({
    queryKey: CAMPAIGN_QUERY_KEY,
    queryFn: listCampaigns,
  });

  const campaigns = useMemo(() => campaignsQuery.data?.data ?? [], [campaignsQuery.data]);

  const form = useForm<CampaignFormInput, unknown, CampaignFormOutput>({
    resolver: zodResolver(campaignFormSchema),
    defaultValues: createDefaultFormValues(),
  });

  const draft = useWatch({ control: form.control, compute: (values) => values });

  useEffect(() => {
    form.reset(toFormValues(editingCampaign));
  }, [editingCampaign, form]);

  const invalidateCampaigns = async () => {
    await queryClient.invalidateQueries({ queryKey: CAMPAIGN_QUERY_KEY });
  };

  const createMutation = useMutation({
    mutationFn: (input: AdminCampaignInput) => createCampaign(input),
    onSuccess: async () => {
      await invalidateCampaigns();
      toast({ title: 'Campaign created' });
      setEditingCampaign(null);
      form.reset(createDefaultFormValues());
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to create campaign', description: error.message, variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: AdminCampaignInput }) => updateCampaign(id, input),
    onSuccess: async () => {
      await invalidateCampaigns();
      toast({ title: 'Campaign updated' });
      setEditingCampaign(null);
      form.reset(createDefaultFormValues());
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to update campaign', description: error.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteCampaign(id),
    onSuccess: async () => {
      await invalidateCampaigns();
      toast({ title: 'Campaign deleted' });
      setDeleteTarget(null);
      if (editingCampaign?.id === deleteTarget?.id) {
        setEditingCampaign(null);
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to delete campaign', description: error.message, variant: 'destructive' });
    },
  });

  const isEditing = editingCampaign !== null;
  const bannerPreviewTheme: CampaignTheme = draft.theme;
  const interstitialPreviewTheme: CampaignTheme = draft.theme;

  const handleSubmit = form.handleSubmit((values) => {
    const input = normalizeCampaign(values);
    if (editingCampaign) {
      updateMutation.mutate({ id: editingCampaign.id, input });
      return;
    }

    createMutation.mutate(input);
  });

  const syncStatusCounts = useMemo(() => {
    return campaigns.reduce<Record<AdminCampaign['stripeSyncStatus'], number>>(
      (accumulator, campaign) => {
        accumulator[campaign.stripeSyncStatus] += 1;
        return accumulator;
      },
      {
        synced: 0,
        pending: 0,
        failed: 0,
        not_applicable: 0,
      },
    );
  }, [campaigns]);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Campaign Manager"
        description="Create, preview, and schedule subscription campaigns."
      />

      <AdminSectionCard
        title="Campaigns"
        description="Manage banner and interstitial campaigns, target tiers, and Stripe sync metadata."
      >
        <div className="overflow-hidden rounded-2xl border border-border/70">
          <table className="w-full text-left text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Slug</th>
                <th className="px-4 py-3">Tiers</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((campaign) => (
                <tr key={campaign.id} className="border-t border-border/70">
                  <td className="px-4 py-4 font-medium">{campaign.name}</td>
                  <td className="px-4 py-4 text-muted-foreground">{campaign.slug}</td>
                  <td className="px-4 py-4">
                    <div className="flex flex-wrap gap-2">
                      {campaign.targetTiers.map((tier) => (
                        <Badge key={tier} variant="outline">
                          {TIER_LABELS[tier]}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-wrap gap-2">
                      <Badge variant={campaign.isActive ? 'success' : 'outline'}>
                        {campaign.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      <Badge variant={getSyncBadgeVariant(campaign.stripeSyncStatus)}>
                        {campaign.stripeSyncStatus}
                      </Badge>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        aria-label={`Edit ${campaign.name}`}
                        onClick={() => setEditingCampaign(campaign)}
                      >
                        Edit
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        aria-label={`Delete ${campaign.name}`}
                        onClick={() => setDeleteTarget(campaign)}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {campaigns.length === 0 ? (
                <tr>
                  <td className="px-4 py-8 text-sm text-muted-foreground" colSpan={5}>
                    No campaigns yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex flex-wrap gap-2 text-xs text-muted-foreground">
          <span>Synced: {syncStatusCounts.synced}</span>
          <span>Pending: {syncStatusCounts.pending}</span>
          <span>Failed: {syncStatusCounts.failed}</span>
          <span>Not applicable: {syncStatusCounts.not_applicable}</span>
        </div>
      </AdminSectionCard>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        <AdminSectionCard
          title={isEditing ? 'Edit campaign' : 'Create campaign'}
          description="Banner, interstitial, email, targeting, schedule, and theme settings."
        >
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="campaign-name">Campaign name</label>
                <Input id="campaign-name" {...form.register('name')} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="campaign-slug">Slug</label>
                <Input id="campaign-slug" {...form.register('slug')} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="campaign-banner-title">Banner title</label>
                <Input id="campaign-banner-title" {...form.register('bannerTitle')} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="campaign-banner-cta-text">Banner CTA text</label>
                <Input id="campaign-banner-cta-text" {...form.register('bannerCtaText')} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <label className="text-sm font-medium" htmlFor="campaign-banner-description">Banner description</label>
                <Textarea id="campaign-banner-description" {...form.register('bannerDescription')} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="campaign-banner-cta-url">Banner CTA URL</label>
                <Input id="campaign-banner-cta-url" {...form.register('bannerCtaUrl')} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="campaign-interstitial-title">Interstitial title</label>
                <Input id="campaign-interstitial-title" {...form.register('interstitialTitle')} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="campaign-interstitial-cta-text">Interstitial CTA text</label>
                <Input id="campaign-interstitial-cta-text" {...form.register('interstitialCtaText')} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <label className="text-sm font-medium" htmlFor="campaign-interstitial-description">Interstitial description</label>
                <Textarea id="campaign-interstitial-description" {...form.register('interstitialDescription')} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="campaign-interstitial-cta-url">Interstitial CTA URL</label>
                <Input id="campaign-interstitial-cta-url" {...form.register('interstitialCtaUrl')} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="campaign-email-subject">Email subject</label>
                <Input id="campaign-email-subject" {...form.register('emailSubject')} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <label className="text-sm font-medium" htmlFor="campaign-email-body">Email template</label>
                <Textarea id="campaign-email-body" {...form.register('emailBody')} />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="campaign-theme-accent">Theme accent</label>
                <Input id="campaign-theme-accent" {...form.register('theme.accent')} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="campaign-theme-background">Theme background</label>
                <Input id="campaign-theme-background" {...form.register('theme.background')} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="campaign-theme-foreground">Theme foreground</label>
                <Input id="campaign-theme-foreground" {...form.register('theme.foreground')} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="campaign-theme-icon">Theme icon</label>
                <select
                  id="campaign-theme-icon"
                  className="flex h-10 w-full rounded-2xl border border-input bg-background px-3 py-2 text-sm"
                  {...form.register('theme.icon', {
                    setValueAs: (value) => (value === '' ? null : value),
                  })}
                >
                  <option value="sparkles">Sparkles</option>
                  <option value="rocket">Rocket</option>
                  <option value="gift">Gift</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="campaign-discount-percent">Discount percent</label>
                <Input id="campaign-discount-percent" type="number" min="1" max="100" {...form.register('discountPercent')} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="campaign-discount-duration-months">Discount duration months</label>
                <Input id="campaign-discount-duration-months" type="number" min="1" {...form.register('discountDurationMonths')} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="campaign-start-date">Start date</label>
                <Input id="campaign-start-date" type="datetime-local" {...form.register('startDate')} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="campaign-end-date">End date</label>
                <Input id="campaign-end-date" type="datetime-local" {...form.register('endDate')} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="campaign-priority">Priority</label>
                <Input id="campaign-priority" type="number" {...form.register('priority')} />
                <p className="text-xs text-muted-foreground">
                  Higher numbers win when more than one campaign is aimed at the same plan. If two
                  campaigns share a number, the one edited most recently wins. The campaign that
                  ships with the product sits at 0.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="text-sm font-medium">Target tiers</div>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {TARGET_TIERS.map((tier) => {
                  const selected = draft.targetTiers.includes(tier);
                  return (
                    <label key={tier} className="flex items-center gap-3 rounded-2xl border border-border/70 px-4 py-3 text-sm">
                      <Checkbox
                        checked={selected}
                        onCheckedChange={(checked) => {
                          const next = checked
                            ? Array.from(new Set([...draft.targetTiers, tier]))
                            : draft.targetTiers.filter((value) => value !== tier);
                          form.setValue('targetTiers', next, { shouldDirty: true, shouldValidate: true });
                        }}
                      />
                      <span>{TIER_LABELS[tier]}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <label className="flex items-center gap-3 rounded-2xl border border-border/70 px-4 py-3 text-sm">
              <Checkbox
                checked={draft.isActive}
                onCheckedChange={(checked) => {
                  form.setValue('isActive', checked, { shouldDirty: true });
                }}
              />
              <span>Campaign active</span>
            </label>

            <div className="flex flex-wrap gap-3">
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                <Plus className="mr-2 h-4 w-4" />
                {isEditing ? 'Save campaign' : 'Create campaign'}
              </Button>
              {isEditing ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditingCampaign(null)}
                >
                  Cancel edit
                </Button>
              ) : null}
            </div>
          </form>
        </AdminSectionCard>

        <div className="space-y-4">
          <CampaignPreview
            surface="banner"
            title={draft.bannerTitle}
            description={draft.bannerDescription}
            ctaText={draft.bannerCtaText}
            theme={bannerPreviewTheme}
          />
          <CampaignPreview
            surface="interstitial"
            title={draft.interstitialTitle}
            description={draft.interstitialDescription}
            ctaText={draft.interstitialCtaText}
            theme={interstitialPreviewTheme}
          />
        </div>
      </div>

      <CampaignEmailPanel campaign={editingCampaign} />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null);
          }
        }}
        title="Delete Campaign"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This cannot be undone.`}
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteMutation.mutate(deleteTarget.id);
        }}
        loading={deleteMutation.isPending}
        data-testid="delete-campaign-dialog"
      />
    </div>
  );
}
